import { ColumnDef } from "@tanstack/react-table";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
  loadDataTableSearch,
} from "@/components/ui/param-data-table";
import { Button } from "@/components/ui/button";
import { Plus, Archive } from "lucide-react";
import {
  useGetBulkBundles,
  getGetBulkBundlesQueryOptions,
} from "@/requests/gen/react-query/bulk-bundle";
import { RequirePermission } from "@/components/universal/require-permission";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import { useState } from "react";
import { Toggle } from "@/components/ui/toggle";
import { format, parseISO } from "date-fns";
import { PermissionEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import { useQueryStates, type inferParserType } from "nuqs";

type BulkBundleSearch = inferParserType<typeof dataTableSearchParsers>;

function bulkBundleQueryParams(
  p: BulkBundleSearch,
): Parameters<typeof getGetBulkBundlesQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    is_deleted: p.is_deleted,
    entity_ids: p.entity_ids,
  };
}

export const Route = createFileRoute(
  "/_private/_practice-360/bulk-bundle/lookup",
)({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetBulkBundlesQueryOptions(
        bulkBundleQueryParams(loadDataTableSearch(location.searchStr)),
      ),
    ),
  component: BulkBundleLookup,
});

// Define the columns headers and accessor keys
interface BulkBundleTableProps {
  link?: LinkProps;
  name: string;
  entity: string;
  created_at: string;
}

// Define the columns headers and accessor keys
const columns: ColumnDef<BulkBundleTableProps>[] = [
  {
    header: "Name",
    accessorKey: "name",
  },
  {
    header: "Entity",
    accessorKey: "entity",
  },
  {
    header: "Created At",
    accessorKey: "created_at",
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) {
        return "";
      }
      return format(parseISO(dateString), "MM-dd-yyyy");
    },

    enableSorting: true,
  },
];

// Main component
function BulkBundleLookup() {
  const [queryStates, setQueryStates] = useQueryStates(dataTableSearchParsers);

  const bulkBundles = useGetBulkBundles(bulkBundleQueryParams(queryStates), {
    query: { placeholderData: keepPreviousData },
  });

  const [entitySearch, setEntitySearch] = useState<string | undefined>();
  const entities = useGetEntitiesInfinite(
    {
      search: entitySearch,
      include_ids: queryStates.entity_ids,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  const tableData: BulkBundleTableProps[] | undefined =
    bulkBundles.data?.items.map((bundle) => {
      return {
        link: linkOptions({
          to: "/bulk-bundle/$bulkBundleId",
          params: { bulkBundleId: String(bundle.bulk_bundle_id) },
        }),
        name: bundle.name,
        entity: bundle.entity.name,
        created_at: bundle.created_at,
      };
    });

  return (
    <div>
      <title>Bulk Bundles - Legrande Health Portal</title>
      <Header
        crumbs={[{ name: "Bulk Bundles", link: { to: "/bulk-bundle/lookup" } }]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Bulk Bundles</CardTitle>
          <CardDescription>
            View your bundles below. You can search, filter, and click to see
            more details.
          </CardDescription>
          <CardAction>
            <RequirePermission
              permission={PermissionEnum.administrate_legrande}
            >
              <Button variant="default" size="sm" asChild>
                <Link to="/bulk-bundle/create">
                  <Plus />
                  Create Bulk Bundle
                </Link>
              </Button>
            </RequirePermission>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={bulkBundles.isFetching} />
            <DropdownFilter
              title="Entity"
              options={[
                {
                  items:
                    entities.data?.pages
                      .flatMap((page) => page.items)
                      .map((entity) => {
                        return {
                          label: entity.name,
                          value: entity.entity_id,
                        };
                      }) ?? [],
                },
              ]}
              search={entitySearch}
              setSearch={setEntitySearch}
              isFetching={entities.isFetchingNextPage}
              fetchNextPage={entities.fetchNextPage}
              values={queryStates.entity_ids}
              onChange={(values) => setQueryStates({ entity_ids: values })}
            />
            <Toggle
              pressed={queryStates.is_deleted || false}
              onPressedChange={(pressed) => {
                setQueryStates({
                  is_deleted: pressed,
                });
              }}
            >
              <Archive />
              Archived
            </Toggle>
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={bulkBundles.isLoading}
            isError={bulkBundles.isError}
            count={bulkBundles.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
